import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import UnifiedHeader from "./UnifiedHeader";
import AdminHeader from "../../Admin/AdminComponents/AdminHeader";
import AdminSidebar from "../../Admin/AdminComponents/AdminSidebar";
import { useAuth } from "../Auth/AuthContext";
import { useLoading } from "../GlobalLoading/LoadingContext";
import debug from '../../utils/debug';

const HeaderManager = () => {
  const { currentUser, isVerified, userType, authReady, navigationReady, authToken } = useAuth();
  const { setIsLoading } = useLoading();
  const location = useLocation();
  const navigate = useNavigate();

  // Simple path checking function
  const pathIncludes = (keywords) => {
    if (!Array.isArray(keywords)) keywords = [keywords];
    return keywords.some(keyword => location.pathname.includes(keyword));
  };
  
  // Check for admin routes
  const isAdminRoute = pathIncludes([
    "/admin-login",
    "/admin-dashboard",
    "/admin-analytics",
    "/admin-users",
    "/admin-bookings",
    "/admin-flow-monitor",
    "/admin-content",
    "/admin-settings",
    "/admin-audit-logs"
  ]);
  
  // Simple route checking 
  const isNoHeaderRoute = pathIncludes([
    "/register", 
    "/login",
    "/epk-layout"
  ]);
  
  const isPublicRoute = [
    "/", 
    "/our-story", 
    "/Explore", 
    "/how-it-works", 
    "/transaction-fees", 
    "/explore-events", 
    "/contact", 
    "/logged-out"
  ].includes(location.pathname);

  // Check if the current path is a blueprint route
  const isBlueprint = location.pathname.startsWith("/talent-blueprint") || 
                      location.pathname.startsWith("/organiser-blueprint");

  // Check if the current path is a dashboard route
  const isDashboard = location.pathname.startsWith("/talent-dashboard") ||
                      location.pathname.startsWith("/organiser-dashboard");

  // Check if we should show the verification alert
  const shouldShowVerificationAlert = currentUser && !isVerified && 
                                      !location.pathname.includes("/verify-email") &&
                                      !isNoHeaderRoute && !isAdminRoute;
                                       
  // Handle redirects for authentication states
  useEffect(() => {
    if (!authReady) return;
    
    // Don't redirect if on public routes, no-header routes, or admin routes
    if (isPublicRoute || isNoHeaderRoute || isAdminRoute) return;
    
    // Don't redirect if already on verification page
    if (location.pathname.includes("/verify-email")) return;
    
    // Don't redirect from blueprint or dashboard regardless of user state
    if (isBlueprint || isDashboard) {
      debug.log("HeaderManager: On blueprint/dashboard route, allowing access regardless of userType");
      return;
    }
    
    // If authenticated but no userType, redirect to register
    if (currentUser && !userType) {
      debug.log("HeaderManager: User authenticated but no userType found, redirecting to register");
      navigate("/register");
      return;
    }
    
    // If authenticated but not verified, redirect to verification page
    if (currentUser && !isVerified && !location.pathname.includes("/verify-email")) {
      debug.log("HeaderManager: User not verified, redirecting to verify-email");
      navigate(`/verify-email?type=${userType || 'Talent'}&uid=${currentUser.uid}`);
      return;
    }
  }, [currentUser, userType, isVerified, authReady, location.pathname, navigate, isAdminRoute, isPublicRoute, isNoHeaderRoute, isBlueprint, isDashboard]);
  
  // Show loading state when navigating between authenticated routes,
  // but only if we don't have cached auth data
  useEffect(() => {
    // Don't show loading for public or no-header routes
    if (isPublicRoute || isNoHeaderRoute) {
      setIsLoading(false);
      return;
    }
    
    // Don't show loading if we have a cached auth token
    if (authToken) {
      setIsLoading(false);
      return;
    }
    
    // Show loading only when auth is initializing for protected routes
    if (!authReady) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }, [authReady, authToken, isPublicRoute, isNoHeaderRoute, setIsLoading]);
  
  // Return null for no header routes
  if (isNoHeaderRoute) return null;
  
  // For admin routes, show AdminHeader and AdminSidebar (but not for admin-login)
  if (isAdminRoute && !location.pathname.includes("/admin-login")) {
    return (
      <>
        <AdminSidebar />
        <AdminHeader />
      </>
    );
  }
  
  // Return null for admin login page
  if (location.pathname.includes("/admin-login")) {
    return null;
  }
  
  // For all other routes, show the unified header with verification alert if needed
  return (
    <>
      {/* Verification Alert */}
      {shouldShowVerificationAlert && (
        <div
          style={{
            backgroundColor: "#f85008",
            color: "#fff",
            textAlign: "center",
            padding: "10px",
            fontFamily: "Montserrat, sans-serif",
            position: "relative",
            zIndex: 1001,
          }}
        >
          Please verify your email to access all features.
        </div>
      )}

      {/* Unified Header Component */}
      <UnifiedHeader />
    </>
  );
};

export default HeaderManager;